import { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

import { PageWithTitle } from 'src/Common/components';
import {
  useUnwindingRules,
  usePanic,
  useGlobalToggle,
  useTogglePlatform
} from '../api/useUnwinding';
import { UnwindingRule, EnrichedRuleStatus, SimulationResult } from '../types/unwinding.types';
import { ExecutionLogTable } from '../components/unwinding/ExecutionLogTable';
import { useUnwindingSocket } from '../hooks/useUnwindingSocket';

const formatPercent = (value: number | undefined | null) => {
  if (value == null || isNaN(value)) return '-';
  return value.toFixed(2) + '%';
};

const headerSx = {
  fontSize: '0.7rem',
  fontWeight: 600,
  color: 'grey.500',
  py: 0.5,
  px: 0.75,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.03em',
  whiteSpace: 'nowrap' as const
};

const groupHeaderSx = {
  ...headerSx,
  textAlign: 'center' as const,
  borderBottom: '1px solid rgba(255,255,255,0.08)'
};

const cellSx = { fontSize: '0.8rem', py: 0.5, px: 0.75 } as const;

export const UnwindingPage = () => {
  const { data: rules, isLoading } = useUnwindingRules();
  const panicMutation = usePanic();
  const globalToggleMutation = useGlobalToggle();
  const togglePlatformMutation = useTogglePlatform();
  const { isConnected, getState } = useUnwindingSocket();

  const [expandedRuleId, setExpandedRuleId] = useState<string | null>(null);

  const globalEnabled = rules?.some(r => r.status === 'active') ?? false;

  return (
    <PageWithTitle>
      <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h5" sx={{ color: 'white' }}>
          Position Unwinding
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip
            icon={<FiberManualRecordIcon sx={{ fontSize: '0.6rem !important' }} />}
            label={isConnected ? 'Live' : 'Disconnected'}
            size="small"
            color={isConnected ? 'success' : 'default'}
            variant="outlined"
            sx={{ fontSize: '0.7rem' }}
          />
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={globalEnabled}
                onChange={(_, v) => globalToggleMutation.mutate(v)}
              />
            }
            label="Global"
            sx={{ '& .MuiTypography-root': { fontSize: '0.75rem' }, mr: 0 }}
          />
          <Button
            variant="contained"
            color="error"
            size="small"
            startIcon={<WarningAmberIcon />}
            onClick={() => {
              if (window.confirm('PANIC: Cancel ALL orders and stop everything?'))
                panicMutation.mutate();
            }}
            sx={{ fontSize: '0.7rem' }}
          >
            PANIC
          </Button>
        </Stack>
      </Stack>

      {/* Rules table */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : !Array.isArray(rules) || rules.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography sx={{ color: 'grey.500', fontSize: '0.9rem' }}>
            No unwinding rules yet. Create one to start unwinding positions.
          </Typography>
        </Box>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              {/* Group header row */}
              <TableRow>
                <TableCell sx={groupHeaderSx} rowSpan={2} />
                <TableCell sx={groupHeaderSx} rowSpan={2}>
                  Market
                </TableCell>
                <TableCell sx={groupHeaderSx} colSpan={4}>
                  Market Data
                </TableCell>
                <TableCell sx={groupHeaderSx} colSpan={3}>
                  Parameters
                </TableCell>
                <TableCell sx={groupHeaderSx} colSpan={2}>
                  Simulation
                </TableCell>
                <TableCell sx={groupHeaderSx} colSpan={2}>
                  Controls
                </TableCell>
              </TableRow>
              {/* Sub-header row */}
              <TableRow>
                <TableCell sx={headerSx} align="right">Poly Bid</TableCell>
                <TableCell sx={headerSx} align="right">Poly Ask</TableCell>
                <TableCell sx={headerSx} align="right">Kal Bid</TableCell>
                <TableCell sx={headerSx} align="right">Kal Ask</TableCell>
                <TableCell sx={headerSx} align="right">TNA%</TableCell>
                <TableCell sx={headerSx} align="right">Size</TableCell>
                <TableCell sx={headerSx} align="right">Max Vol</TableCell>
                <TableCell sx={headerSx} align="right">Sim Poly</TableCell>
                <TableCell sx={headerSx} align="right">Sim Kalshi</TableCell>
                <TableCell sx={headerSx} align="center">Poly</TableCell>
                <TableCell sx={headerSx} align="center">Kalshi</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rules.map(rule => (
                <RuleRow
                  key={rule._id}
                  rule={rule}
                  liveState={getState(rule._id)}
                  expanded={expandedRuleId === rule._id}
                  onToggleExpand={() =>
                    setExpandedRuleId(
                      expandedRuleId === rule._id ? null : rule._id
                    )
                  }
                  onTogglePlatform={(platform, enabled) =>
                    togglePlatformMutation.mutate({ id: rule._id, platform, enabled })
                  }
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

    </PageWithTitle>
  );
};

/* ------------------------------------------------------------------ */

interface RuleRowProps {
  rule: UnwindingRule;
  liveState?: EnrichedRuleStatus;
  expanded: boolean;
  onToggleExpand: () => void;
  onTogglePlatform: (platform: 'kalshi' | 'polymarket', enabled: boolean) => void;
}

const TOTAL_COLUMNS = 13;

const RuleRow = ({
  rule,
  liveState,
  expanded,
  onToggleExpand,
  onTogglePlatform
}: RuleRowProps) => {
  return (
    <>
      <TableRow
        sx={{
          '&:hover': { backgroundColor: 'rgba(255,255,255,0.03)' },
          cursor: 'pointer'
        }}
        onClick={onToggleExpand}
      >
        {/* Expand icon */}
        <TableCell sx={{ ...cellSx, width: 32, px: 0.5 }}>
          <IconButton size="small" sx={{ p: 0.3 }}>
            {expanded ? (
              <ExpandLessIcon sx={{ fontSize: '1rem' }} />
            ) : (
              <ExpandMoreIcon sx={{ fontSize: '1rem' }} />
            )}
          </IconButton>
        </TableCell>

        {/* Market title -- two rows with platform logos */}
        <TableCell sx={{ ...cellSx, minWidth: 180 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <a
                href={liveState?.polymarketMarketUrl ?? undefined}
                target="_blank"
                rel="noopener"
                onClick={e => e.stopPropagation()}
              >
                <img
                  src="/assets/exchanges/png/100x100/polymarket.png"
                  alt="Poly"
                  style={{ width: 16, height: 16, borderRadius: '50%' }}
                />
              </a>
              <Typography sx={{ fontSize: '0.75rem' }} noWrap>
                {rule.pairName}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <a
                href={liveState?.kalshiMarketUrl ?? undefined}
                target="_blank"
                rel="noopener"
                onClick={e => e.stopPropagation()}
              >
                <img
                  src="/assets/exchanges/png/100x100/kalshi.png"
                  alt="Kalshi"
                  style={{ width: 16, height: 16, borderRadius: '50%' }}
                />
              </a>
              <Typography sx={{ fontSize: '0.7rem', color: 'grey.500' }} noWrap>
                {rule.kalshiConfig?.ticker || rule.pairName}
              </Typography>
            </Box>
          </Box>
        </TableCell>

        {/* Poly Bid */}
        <TableCell sx={cellSx} align="right">
          <PriceCell price={liveState?.polyBid?.price} size={liveState?.polyBid?.size} />
        </TableCell>

        {/* Poly Ask */}
        <TableCell sx={cellSx} align="right">
          <PriceCell price={liveState?.polyAsk?.price} size={liveState?.polyAsk?.size} />
        </TableCell>

        {/* Kalshi Bid */}
        <TableCell sx={cellSx} align="right">
          <PriceCell price={liveState?.kalshiBid?.price} size={liveState?.kalshiBid?.size} />
        </TableCell>

        {/* Kalshi Ask */}
        <TableCell sx={cellSx} align="right">
          <PriceCell price={liveState?.kalshiAsk?.price} size={liveState?.kalshiAsk?.size} />
        </TableCell>

        {/* TNA% */}
        <TableCell sx={cellSx} align="right">
          <Typography sx={{ fontSize: '0.8rem' }}>
            {formatPercent(
              rule.kalshiConfig?.targetTnaPercent ??
                rule.polymarketConfig?.targetTnaPercent
            )}
          </Typography>
        </TableCell>

        {/* Size */}
        <TableCell sx={cellSx} align="right">
          <Typography sx={{ fontSize: '0.8rem' }}>
            {rule.kalshiConfig?.contractsToSell ??
              rule.polymarketConfig?.contractsToSell ??
              '-'}
          </Typography>
        </TableCell>

        {/* Max Vol */}
        <TableCell sx={cellSx} align="right">
          <Typography sx={{ fontSize: '0.8rem' }}>
            ${rule.maxVolumeUsd}/{rule.volumePeriod}
          </Typography>
        </TableCell>

        {/* Sim Poly (poly as maker) */}
        <TableCell sx={cellSx} align="right">
          <SimCell sim={liveState?.simulation?.polyAsMaker ?? null} />
        </TableCell>

        {/* Sim Kalshi (kalshi as maker) */}
        <TableCell sx={cellSx} align="right">
          <SimCell sim={liveState?.simulation?.kalshiAsMaker ?? null} />
        </TableCell>

        {/* Poly toggle */}
        <TableCell sx={cellSx} align="center">
          <Switch
            size="small"
            checked={rule.polymarketEnabled !== false}
            onChange={(_, v) => onTogglePlatform('polymarket', v)}
            onClick={e => e.stopPropagation()}
          />
        </TableCell>

        {/* Kalshi toggle */}
        <TableCell sx={cellSx} align="center">
          <Switch
            size="small"
            checked={rule.kalshiEnabled !== false}
            onChange={(_, v) => onTogglePlatform('kalshi', v)}
            onClick={e => e.stopPropagation()}
          />
        </TableCell>
      </TableRow>

      {/* Expanded execution log */}
      <TableRow>
        <TableCell
          sx={{ p: 0, borderBottom: expanded ? undefined : 'none' }}
          colSpan={TOTAL_COLUMNS}
        >
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ px: 2, py: 1 }}>
              <ExecutionLogTable ruleId={rule._id} />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

/* ------------------------------------------------------------------ */
/* Helper components                                                   */
/* ------------------------------------------------------------------ */

const PriceCell = ({
  price,
  size
}: {
  price: number | undefined | null;
  size: number | undefined | null;
}) => (
  <Box sx={{ textAlign: 'right' }}>
    <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
      {price != null ? price.toFixed(2) : '\u2014'}
    </Typography>
    <Typography sx={{ fontSize: '0.65rem', color: 'grey.500' }}>
      {size ?? ''}
    </Typography>
  </Box>
);

const SimCell = ({ sim }: { sim: SimulationResult | null }) => (
  <Box sx={{ textAlign: 'right' }}>
    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'info.main' }}>
      {sim?.makerPrice != null ? sim.makerPrice.toFixed(2) : '\u2014'}
    </Typography>
    <Typography sx={{ fontSize: '0.65rem', color: 'grey.500' }}>
      {sim?.takerPrice != null ? sim.takerPrice.toFixed(2) : ''}
    </Typography>
    <Typography sx={{ fontSize: '0.6rem', color: 'success.main' }}>
      {sim?.tna != null ? `${sim.tna.toFixed(1)}%` : ''}
    </Typography>
  </Box>
);
